import type { PollCreateInput } from "@/entities/post/types";
import type { AltchaCredential } from "@/features/post/lib/altchaChallengeController";
import type { PostAttemptStore } from "@/features/post/lib/postAttemptStore";
import {
  getAttachmentSource,
  readAttachmentForUpload,
  sha256,
} from "@/shared/api/attachmentUpload";
import { boardApiPath } from "@/shared/api/boardPath";
import { ApiError } from "@/shared/api/errors";
import type { CreatePostResult } from "@/shared/api/types";

export type ModerationLevel = "off" | "lenient" | "standard" | "strict";

export type ThreadModerationInput = {
  text: {
    sexual: "off" | "on";
    exclusion: ModerationLevel;
    abuse: ModerationLevel;
  };
  image: { nsfw: "off" | "on" };
};

export type SubmitPostInput = {
  mode: "thread" | "reply";
  threadId?: string;
  body: string;
  deleteKey: string;
  /** Ordered attachment files. The backend validates count and media rules. */
  files: File[];
  /**
   * スレ立て時の検閲設定。R18タグはサーバー側でこの値から導出される
   * (R-18画像を許可するとR18タグが付く。性的な文章は導出に関与しない)。
   */
  moderation?: ThreadModerationInput;
  allowImageReplies?: boolean;
  duration?: string;
  /** スレッド内の全投稿へスレッド単位の表示IDを付与する */
  showId?: boolean;
  /** レス投稿またはスレッドsage進行の指定 */
  sage?: boolean;
  /** スレ主本人として表示する指定。本人性はバックエンドで検証する。 */
  showThreadOwner?: boolean;
  poll?: PollCreateInput;
  /**
   * 投稿対象の板slug。スレ立てでは立て先(総合カタログの板選択)、返信では
   * スレの所属板を渡す。省略時は現在板。presign〜投稿まで同じ板keyで
   * 一貫させる(添付presignはX-Board-Keyだけで板が決まるため、mix配下の
   * 他板スレへの返信で省略すると422 ATTACHMENT_NOT_UPLOADEDになる)。
   */
  boardKey?: string;
  signal?: AbortSignal;
};

export type AltchaCredentialProvider = {
  getCredential: (signal?: AbortSignal) => Promise<AltchaCredential>;
  reset: () => void;
};

export type SubmitPostFlowDependencies = {
  attempts?: PostAttemptStore;
  uploadAttachment: (
    file: File,
    signal?: AbortSignal,
    boardKey?: string,
  ) => Promise<string>;
  post: (
    path: string,
    body: object,
    options: { token: string; signal?: AbortSignal; boardKey?: string },
  ) => Promise<CreatePostResult>;
  refreshToken: (expiredToken: string) => Promise<string>;
  invalidateMultiAttachmentCapability?: () => void;
};

export async function submitPostFlow(
  {
    mode,
    threadId,
    body,
    deleteKey,
    files,
    moderation,
    allowImageReplies = true,
    duration = "",
    showId = false,
    sage = false,
    showThreadOwner = false,
    poll,
    boardKey,
    signal,
  }: SubmitPostInput,
  credentialProvider: AltchaCredentialProvider,
  dependencies: SubmitPostFlowDependencies,
): Promise<CreatePostResult> {
  if (mode === "reply" && !threadId) {
    throw new Error("スレッドIDがありません");
  }

  signal?.throwIfAborted();
  const targetBoardKey = boardKey;
  const attachmentSources = files.map(getAttachmentSource);
  const attempt = dependencies.attempts
    ? await dependencies.attempts.begin(
        {
          mode,
          threadId,
          body,
          deleteKey,
          files,
          moderation,
          allowImageReplies,
          duration,
          showId,
          sage,
          showThreadOwner,
          poll,
          boardKey,
          signal,
        },
        await Promise.all(
          files.map(async (file) => ({
            digest: await sha256(await readAttachmentForUpload(file)),
            name: file.name,
            type: file.type,
            source: getAttachmentSource(file),
          })),
        ),
        sha256,
      )
    : undefined;
  try {
    signal?.throwIfAborted();
  } catch (error) {
    attempt?.finish(true);
    throw error;
  }
  const uploads = new AbortController();
  const uploadSignal = signal
    ? AbortSignal.any([signal, uploads.signal])
    : uploads.signal;
  const uploadResults = await Promise.allSettled(
    files.map(async (file, index) => {
      const completed = attempt?.value.uploads[index];
      if (completed) return completed;
      try {
        const upload = (): Promise<string> =>
          dependencies.uploadAttachment(file, uploadSignal, targetBoardKey);
        return await (attempt ? attempt.upload(index, upload) : upload());
      } catch (error) {
        uploads.abort(error);
        throw error;
      }
    }),
  );
  if (uploads.signal.aborted) {
    attempt?.finish(true);
    throw uploads.signal.reason;
  }
  const attachmentIds = uploadResults.map((result) => {
    if (result.status === "rejected") throw result.reason;
    return result.value;
  });
  const path =
    mode === "thread"
      ? targetBoardKey !== undefined
        ? `/threads/${encodeURIComponent(targetBoardKey)}`
        : boardApiPath("/threads")
      : `/threads/${threadId}/posts`;
  const basePayload = {
    ...(attempt ? { requestId: attempt.value.requestId } : {}),
    body,
    deleteKey,
    ...(attachmentIds.length === 1 ? { attachmentId: attachmentIds[0] } : {}),
    ...(attachmentIds.length > 1 ? { attachmentIds } : {}),
    ...(attachmentSources.length > 0 ? { attachmentSources } : {}),
    ...(poll ? { poll } : {}),
    ...(mode === "thread"
      ? {
          ...(moderation ? { moderation } : {}),
          allowImageReplies,
          duration,
          sage,
          showId,
        }
      : {
          ...(sage ? { sage: true } : {}),
          ...(showThreadOwner ? { showThreadOwner: true } : {}),
        }),
  };

  let didRetryToken = false;
  try {
    while (true) {
      const credential = await getCredentialWithSignal(
        credentialProvider,
        signal,
      );
      try {
        const result = await postWithCredential(
          path,
          basePayload,
          credential,
          signal,
          dependencies,
          targetBoardKey,
        );
        attempt?.finish(false);
        return result;
      } catch (error) {
        signal?.throwIfAborted();
        if (attachmentIds.length > 1 && isUnsupportedApiVersion(error)) {
          dependencies.invalidateMultiAttachmentCapability?.();
        }
        if (shouldRefreshToken(error) && !didRetryToken) {
          didRetryToken = true;
          credentialProvider.reset();
          await dependencies.refreshToken(credential.token);
          signal?.throwIfAborted();
          continue;
        }
        // PoWや認証の棄却も、並行した同keyのcommitを否定できない。
        // 添付を破棄する確定的な棄却だけ、次attemptで再予約する。
        if (
          error instanceof ApiError &&
          (error.code === "ATTACHMENT_INVALID" ||
            error.code === "ATTACHMENT_NOT_UPLOADED" ||
            error.code === "MODERATION_UNAVAILABLE" ||
            error.code === "SPAM_REJECTED" ||
            error.code === "NSFW_REJECTED" ||
            error.code === "POST_RATE_LIMITED" ||
            error.code === "RATE_LIMITED" ||
            error.code === "POLL_CREATION_RATE_LIMITED" ||
            error.code === "NOT_FOUND" ||
            error.code === "THREAD_ARCHIVED" ||
            error.code === "THREAD_CLOSED" ||
            error.code === "IMAGE_REPLIES_DISABLED" ||
            error.code === "THREAD_FULL" ||
            error.code === "DUPLICATE_POST" ||
            error.code === "INVALID_DURATION")
        )
          attempt?.finish(false);
        throw error;
      }
    }
  } catch (error) {
    attempt?.finish(true);
    throw error;
  } finally {
    credentialProvider.reset();
  }
}

async function getCredentialWithSignal(
  credentialProvider: AltchaCredentialProvider,
  signal: AbortSignal | undefined,
): Promise<AltchaCredential> {
  try {
    signal?.throwIfAborted();
    const credential = await credentialProvider.getCredential(signal);
    signal?.throwIfAborted();
    return credential;
  } catch (error) {
    // A widget reset can surface as null/a generic error. If the form lifecycle
    // was cancelled, normalize it to AbortError for the shared toast guard.
    signal?.throwIfAborted();
    throw error;
  }
}

function postWithCredential(
  path: string,
  basePayload: object,
  credential: AltchaCredential,
  signal: AbortSignal | undefined,
  dependencies: SubmitPostFlowDependencies,
  boardKey?: string,
): Promise<CreatePostResult> {
  signal?.throwIfAborted();
  return dependencies.post(
    path,
    { ...basePayload, altcha: credential.altcha },
    {
      token: credential.token,
      signal,
      boardKey,
    },
  );
}

function shouldRefreshToken(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    (error.code === "TOKEN_EXPIRED" || error.code === "TOKEN_NOT_FOUND")
  );
}

function isUnsupportedApiVersion(error: unknown): boolean {
  return error instanceof ApiError && error.code === "UNSUPPORTED_API_VERSION";
}
